import { useContext, useEffect, useState } from "react";
import { ListGroup } from "reactstrap";
import { useSelector } from "react-redux";
import { useMsal } from "@azure/msal-react";
import { useForm } from "react-hook-form";
import {
  Button,
  Modal,
  ModalBody,
  Form,
  FormGroup,
  ListGroupItem,
  ButtonGroup,
  Row,
  Col,
} from "reactstrap";
import { toPng } from "html-to-image";
import { FormContext } from "../context/FormContext";
import { ToolContext } from "../context/ToolContext";
import Spinner from "./Spinner";

import { saveFeedback } from "../services/apiService";
import { buildNetwork, NetworkState } from "../app/networkSlice";
import useUserInfoAndLogging from "../utils/useUserInfoAndLogging";
import useBearerToken from "../utils/useBearerToken";
import { AppState } from "../app/settingsSlice";
import { UndoableState } from "../app/undoable";

const FeedbackForm = () => {
  const { instance, accounts } = useMsal();
  const { logException } = useUserInfoAndLogging();
  const getToken = useBearerToken();
  const { REACT_APP_B2B, REACT_APP_B2C } = process.env;
  const b2x = REACT_APP_B2B === "true" || REACT_APP_B2C === "true";

  const [includeScreenshot, setIncludeScreenshot] = useState(true);
  const [includeNetwork, setIncludeNetwork] = useState(true);
  const [includeFiles, setIncludeFiles] = useState(false);
  const [screenshot, setScreenshot] = useState<string>("");
  const [feedbackErrors, setFeedbackErrors] = useState<{ description: string }[]>([]);
  const { formState } = useContext(FormContext);
  const { toolState, setToolState } = useContext(ToolContext);
  const { network, importedFiles } = formState;
  const { feedbackForm } = toolState;

  const builtNetwork = useSelector(
    (state: { settings: AppState; network: UndoableState<NetworkState> }) => buildNetwork(state),
  );

  useEffect(() => {
    if (feedbackForm) {
      saveScreenshot();
    }
  }, [feedbackForm]);

  type ImportedFile = {
    filename: string;
    content: string;
  };

  interface FeedbackFormFields {
    ContactName: string;
    EmailAddress: string;
    Feedback: string;
    Screenshot?: string;
    Network?: string;
    ImportedFiles?: ImportedFile[];
    UserAgent?: string;
  }

  const { handleSubmit, register, errors } = useForm<FeedbackFormFields>();

  const [isLoading, setIsLoading] = useState(false);

  const onSubmit = async (jsonRequest: FeedbackFormFields) => {
    setIsLoading(true);
    try {
      network.loaded = true;
      includeScreenshot && (jsonRequest.Screenshot = screenshot);
      includeNetwork && (jsonRequest.Network = JSON.stringify({ ...builtNetwork, ...network }));
      includeFiles &&
        (jsonRequest.ImportedFiles = importedFiles.map((f: ImportedFile) => {
          return { Filename: f.filename, Content: f.content };
        }));

      jsonRequest.UserAgent = navigator.userAgent;

      const token = await getToken(instance, accounts, b2x);
      const response = await saveFeedback(token, jsonRequest);

      if (response instanceof Error) {
        logException(response);
      } else if (response.messages && response.messages.length > 0) {
        setFeedbackErrors(response.messages);
      } else {
        toggle();
      }
    } catch (error) {
      logException(error);
    } finally {
      setIsLoading(false);
    }
  };

  const toggle = () => {
    const _toolState = toolState;
    _toolState.feedbackForm = !toolState.feedbackForm;
    setToolState(_toolState);
    setIncludeFiles(false);
    setFeedbackErrors([]);
  };

  const saveScreenshot = () => {
    const printArea = document.getElementById("root");

    if (printArea) {
      toPng(printArea)
        .then((dataURL) => {
          setScreenshot(dataURL);
          const thumbnail = document.getElementById("thumbnail") as HTMLImageElement;
          if (thumbnail) {
            thumbnail.src = dataURL;
          }
        })
        .catch((error) => {
          logException(error);
        });
    }
  };

  const importedFilenames = (importedFiles as ImportedFile[])?.map((f) => f.filename) ?? [];
  const importedFilesAreAvailable = !!importedFilenames.length;

  return (
    <Modal
      isOpen={feedbackForm}
      toggle={toggle}
      size={"lg"}
      className="modal-dialog-centered"
      contentClassName="feedbackForm text-light"
    >
      {isLoading && <Spinner>Reporting feedback</Spinner>}
      <ListGroupItem className="bg-dark" style={{ textTransform: "uppercase" }}>
        Report Feedback
      </ListGroupItem>
      <ModalBody>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <FormGroup>
            <input
              type="text"
              name="ContactName"
              id="ContactName"
              className="form-control form-control-sm"
              placeholder="Name"
              ref={register({
                required: "Required",
              })}
            />
            {errors.ContactName && (
              <div className="invalid-feedback" style={{ display: "block" }}>
                {errors.ContactName.message}
              </div>
            )}
          </FormGroup>
          <FormGroup>
            <input
              type="email"
              name="EmailAddress"
              id="EmailAddress"
              className="form-control form-control-sm"
              placeholder="Email"
              ref={register({
                required: "Required",
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
                  message: "invalid email address",
                },
              })}
            />
            {errors.EmailAddress && (
              <div className="invalid-feedback" style={{ display: "block" }}>
                {errors.EmailAddress.message}
              </div>
            )}
          </FormGroup>
          <FormGroup>
            <textarea
              name="Feedback"
              id="Feedback"
              className="form-control form-control-sm"
              placeholder="Feedback"
              rows={3}
              ref={register({
                required: "Required",
              })}
            ></textarea>
            {errors.Feedback && (
              <div className="invalid-feedback" style={{ display: "block" }}>
                {errors.Feedback.message}
              </div>
            )}
          </FormGroup>
          <Row noGutters>
            <Col className="mr-1">
              <FormGroup>
                <Button
                  color="dark"
                  size="sm"
                  block
                  onClick={() => setIncludeScreenshot(!includeScreenshot)}
                  className={includeScreenshot ? "active" : ""}
                  disabled={!screenshot}
                >
                  <div className="d-flex justify-content-between align-items-center">
                    <span>Include Screenshot</span>
                    <i className={`icon-${includeScreenshot ? "check" : "cross"}-solid`}></i>
                  </div>
                </Button>
              </FormGroup>
            </Col>
            <Col className="mx-1">
              <FormGroup>
                <Button
                  color="dark"
                  size="sm"
                  block
                  onClick={() => setIncludeNetwork(!includeNetwork)}
                  className={includeNetwork ? "active" : ""}
                >
                  <div className="d-flex justify-content-between align-items-center">
                    <span>Include Network</span>
                    <i className={`icon-${includeNetwork ? "check" : "cross"}-solid`}></i>
                  </div>
                </Button>
              </FormGroup>
            </Col>
            <Col className="ml-1">
              {importedFilesAreAvailable && (
                <FormGroup>
                  <Button
                    color="dark"
                    size="sm"
                    block
                    onClick={() => setIncludeFiles(!includeFiles)}
                    className={includeFiles ? "active" : ""}
                  >
                    <div className="d-flex justify-content-between align-items-center">
                      <span>Include Imported Files</span>
                      <i className={`icon-${includeFiles ? "check" : "cross"}-solid`}></i>
                    </div>
                  </Button>
                </FormGroup>
              )}
            </Col>
          </Row>
          {importedFilesAreAvailable && includeFiles && (
            <ListGroup className="mb-3">
              {importedFilenames.map((f) => (
                <ListGroupItem className="bg-info rounded">{f}</ListGroupItem>
              ))}
            </ListGroup>
          )}
          <div className="mb-3" style={{ display: includeScreenshot ? "block" : "none" }}>
            <img id="thumbnail" src="" width="100%" height="100%" alt="Screenshot preview" />
          </div>
          {feedbackErrors.length > 1 && (
            <div className="bg-danger text-white p-2 mb-3">
              {feedbackErrors.length > 1 ? (
                <ul>
                  {feedbackErrors.map((message) => (
                    <li>{message.description}</li>
                  ))}
                </ul>
              ) : (
                feedbackErrors[0].description
              )}
            </div>
          )}
          <ButtonGroup className="float-right">
            <Button color="primary" size="sm" type="submit" className="mr-2">
              Submit
            </Button>
            <Button color="secondary" size="sm" onClick={toggle}>
              Cancel
            </Button>
          </ButtonGroup>
        </Form>
      </ModalBody>
    </Modal>
  );
};

export default FeedbackForm;
